import type { Checklist, ChecklistKey, FlowStepState, Job, JobStatus } from './domain.js';

export interface FlowStep {
  id: string;
  label: string;
  order: number;
  optional: boolean;
  hint?: string;
  icon?: string;
  checklistKeys?: ChecklistKey[];
}

export interface FlowDefinition {
  id: string;
  label: string;
  description: string;
  steps: FlowStep[];
  requiredChecklist: ChecklistKey[];
  suggestedCards: string[];
  commentTemplate: string;
  defaultStatus: JobStatus;
}

export const FLOW_DEFINITIONS: FlowDefinition[] = [
  {
    id: 'turn-on-gas',
    label: 'Turn On Gas',
    description: 'Restore service with a hard leak-check gate, appliance cycle verification, and a clean safety release.',
    defaultStatus: 'Onsite',
    steps: [
      {
        id: 'cgi-leak-check',
        label: 'CGI leak check',
        order: 1,
        optional: false,
        hint: 'Survey before introducing gas and log any abnormal odor or response.',
        icon: 'scan',
        checklistKeys: ['appliance_connected_or_branch_capped']
      },
      {
        id: 'meter-set-verify',
        label: 'Meter set verify',
        order: 2,
        optional: false,
        hint: 'Confirm meter sizing, routing, and regulator sanity for connected load.',
        icon: 'meter',
        checklistKeys: ['meter_sized_for_load', 'manifold_cross_meter_checked']
      },
      {
        id: 'appliance-cycle',
        label: 'Appliance cycle',
        order: 3,
        optional: false,
        hint: 'Cycle connected equipment and verify venting before release.',
        icon: 'flame',
        checklistKeys: ['appliance_visual_inspection_gs6500_060', 'vents_ok', 'gas_on']
      },
      {
        id: 'safety-complete',
        label: 'Safety complete',
        order: 4,
        optional: false,
        hint: 'Customer handoff, site secure, and final safety checks complete.',
        icon: 'shield',
        checklistKeys: ['safety_checks_complete']
      }
    ],
    requiredChecklist: [
      'appliance_connected_or_branch_capped',
      'meter_sized_for_load',
      'appliance_visual_inspection_gs6500_060',
      'vents_ok',
      'safety_checks_complete',
      'gas_on'
    ],
    suggestedCards: ['card-leak-test-procedure', 'card-meter-sizing', 'card-purge-procedure'],
    commentTemplate: 'CGI survey complete. Meter sizing verified. Appliances cycled. Safety release complete.'
  },
  {
    id: 'leak-odor',
    label: 'Leak / Odor',
    description: 'Bias toward containment, classification, repair verification, and a proof packet before release.',
    defaultStatus: 'Onsite',
    steps: [
      {
        id: 'cgi-survey',
        label: 'CGI survey',
        order: 1,
        optional: false,
        hint: 'Run the survey first and document concentration or no-gas findings.',
        icon: 'alert',
        checklistKeys: ['safety_checks_complete']
      },
      {
        id: 'classify-condition',
        label: 'Classify condition',
        order: 2,
        optional: false,
        hint: 'Determine severity, isolate if needed, and capture the reasoning.',
        icon: 'flag'
      },
      {
        id: 'repair-verify',
        label: 'Repair / verify',
        order: 3,
        optional: false,
        hint: 'Repair or verify stable condition, then retest the affected section.',
        icon: 'wrench',
        checklistKeys: ['manifold_cross_meter_checked']
      },
      {
        id: 'proof-packet',
        label: 'Proof packet',
        order: 4,
        optional: false,
        hint: 'Notes, photos, and final disposition ready for closeout.',
        icon: 'proof'
      }
    ],
    requiredChecklist: ['manifold_cross_meter_checked', 'safety_checks_complete'],
    suggestedCards: ['card-leak-test-procedure', 'card-odor-classification', 'card-regulator-vent'],
    commentTemplate: 'CGI survey completed. Condition classified. Repair or verification documented. Proof packet attached.'
  },
  {
    id: 'relight',
    label: 'Relight',
    description: 'Pilot and ignition verification with pressure, draft, and cycle testing before release.',
    defaultStatus: 'Onsite',
    steps: [
      {
        id: 'pilot-ignition-check',
        label: 'Pilot / ignition check',
        order: 1,
        optional: false,
        hint: 'Verify ignition source, pilot stability, and reset behavior.',
        icon: 'spark',
        checklistKeys: ['appliance_visual_inspection_gs6500_060']
      },
      {
        id: 'gas-pressure',
        label: 'Gas pressure',
        order: 2,
        optional: false,
        hint: 'Record inlet/manifold pressure if needed for the appliance condition.',
        icon: 'gauge',
        checklistKeys: ['meter_sized_for_load']
      },
      {
        id: 'combustion-draft',
        label: 'Combustion draft',
        order: 3,
        optional: false,
        hint: 'Confirm draft, spill response, and vent path integrity.',
        icon: 'draft',
        checklistKeys: ['vents_ok']
      },
      {
        id: 'cycle-test',
        label: 'Cycle test',
        order: 4,
        optional: false,
        hint: 'Run a full cycle and confirm safe shutdown or stable operation.',
        icon: 'cycle',
        checklistKeys: ['gas_on', 'safety_checks_complete']
      }
    ],
    requiredChecklist: [
      'appliance_visual_inspection_gs6500_060',
      'meter_sized_for_load',
      'vents_ok',
      'safety_checks_complete',
      'gas_on'
    ],
    suggestedCards: ['card-relight-sequence', 'card-combustion-air-confined', 'card-vent-termination'],
    commentTemplate: 'Ignition checked. Pressure verified. Draft confirmed. Appliance cycled and released safe.'
  },
  {
    id: 'meter-set',
    label: 'Meter Set',
    description: 'Regulator, pressure, purge, and appliance connection workflow for meter set work.',
    defaultStatus: 'Onsite',
    steps: [
      {
        id: 'regulator-check',
        label: 'Regulator check',
        order: 1,
        optional: false,
        hint: 'Confirm venting, orientation, and lock-up behavior.',
        icon: 'reg',
        checklistKeys: ['manifold_tagged_gs6500_100']
      },
      {
        id: 'pressure-test',
        label: 'Pressure test',
        order: 2,
        optional: false,
        hint: 'Test the piping section before introducing gas.',
        icon: 'pressure',
        checklistKeys: ['disconnected_outlets_capped']
      },
      {
        id: 'purge',
        label: 'Purge',
        order: 3,
        optional: false,
        hint: 'Purge to an approved release path with ignition sources controlled.',
        icon: 'purge',
        checklistKeys: ['appliance_connected_or_branch_capped']
      },
      {
        id: 'appliance-connect',
        label: 'Appliance connect',
        order: 4,
        optional: false,
        hint: 'Reconnect, cycle, and verify final load.',
        icon: 'link',
        checklistKeys: ['meter_sized_for_load', 'gas_on']
      }
    ],
    requiredChecklist: [
      'manifold_tagged_gs6500_100',
      'disconnected_outlets_capped',
      'appliance_connected_or_branch_capped',
      'meter_sized_for_load',
      'gas_on'
    ],
    suggestedCards: ['card-regulator-vent', 'card-leak-test-procedure', 'card-purge-procedure'],
    commentTemplate: 'Regulator verified. Pressure test passed. Purge completed. Appliance connection restored and checked.'
  },
  {
    id: 'appliance-connect',
    label: 'Appliance Connect',
    description: 'Connection workflow with shutoff, union, trap, connector, and bonding verification.',
    defaultStatus: 'Onsite',
    steps: [
      {
        id: 'shutoff',
        label: 'Shutoff valve',
        order: 1,
        optional: false,
        hint: 'Verify shutoff placement, access, and appliance isolation.',
        icon: 'valve'
      },
      {
        id: 'union',
        label: 'Union / connector',
        order: 2,
        optional: false,
        hint: 'Inspect union alignment and approved connector use.',
        icon: 'link',
        checklistKeys: ['appliance_connected_or_branch_capped']
      },
      {
        id: 'sediment-trap',
        label: 'Sediment trap',
        order: 3,
        optional: false,
        hint: 'Confirm trap orientation and code-compliant placement.',
        icon: 'trap'
      },
      {
        id: 'csst-bonding',
        label: 'CSST bonding',
        order: 4,
        optional: false,
        hint: 'Check bonding path and note missing or damaged grounding.',
        icon: 'bond',
        checklistKeys: ['safety_checks_complete', 'gas_on']
      }
    ],
    requiredChecklist: ['appliance_connected_or_branch_capped', 'safety_checks_complete', 'gas_on'],
    suggestedCards: ['card-appliance-connect', 'card-csst-bonding', 'card-regulator-vent'],
    commentTemplate: 'Shutoff and union verified. Sediment trap confirmed. Connector and bonding checks complete.'
  },
  {
    id: 'no-access-meter-check',
    label: 'No Access / Meter Check',
    description: 'Document non-entry or meter-only work with clear evidence and next action.',
    defaultStatus: 'Hold',
    steps: [
      {
        id: 'site-arrival',
        label: 'Site arrival',
        order: 1,
        optional: false,
        hint: 'Confirm address and access condition.',
        icon: 'arrive'
      },
      {
        id: 'meter-only',
        label: 'Meter only pass',
        order: 2,
        optional: false,
        hint: 'Capture what was visible from the accessible area.',
        icon: 'meter'
      },
      {
        id: 'customer-contact',
        label: 'Customer contact',
        order: 3,
        optional: true,
        hint: 'Document attempts to contact the customer or occupant.',
        icon: 'call'
      },
      {
        id: 'next-action',
        label: 'Next action',
        order: 4,
        optional: false,
        hint: 'Record hold reason and follow-up direction.',
        icon: 'hold'
      }
    ],
    requiredChecklist: [],
    suggestedCards: ['card-meter-sizing'],
    commentTemplate: 'No access documented. Meter-only observations captured. Follow-up disposition noted.'
  }
];

export const FLOW_PRESETS = FLOW_DEFINITIONS;

const FLOW_MAP = new Map(FLOW_DEFINITIONS.map((flow) => [flow.id, flow]));
const FLOW_ALIASES: Record<string, string> = {
  turn_on_gas: 'turn-on-gas',
  leak_odor_check: 'leak-odor',
  appliance_cycle_safety: 'relight',
  manifold_meter_verify: 'meter-set',
  no_access_meter_check: 'no-access-meter-check'
};

export function getFlow(flowId: string | null | undefined): FlowDefinition | null {
  if (!flowId) return null;
  const normalized = FLOW_ALIASES[flowId] ?? flowId;
  return FLOW_MAP.get(normalized) ?? null;
}

export function normalizeFlowId(flowId: string | null | undefined): string | null {
  if (!flowId) return null;
  return getFlow(flowId)?.id ?? null;
}

export function createInitialFlowStepStates(flow: FlowDefinition, existing: Record<string, FlowStepState> = {}): Record<string, FlowStepState> {
  const next: Record<string, FlowStepState> = {};
  const ordered = [...flow.steps].sort((left, right) => left.order - right.order);
  let hasActive = false;

  for (const step of ordered) {
    const preset = existing[step.id];
    if (preset) {
      next[step.id] = preset;
      if (preset === 'in_progress') hasActive = true;
      continue;
    }
    if (!hasActive) {
      next[step.id] = 'in_progress';
      hasActive = true;
    } else {
      next[step.id] = 'todo';
    }
  }

  return next;
}

export function areChecklistKeysComplete(checklist: Checklist, keys: ChecklistKey[] | undefined): boolean {
  if (!keys || keys.length === 0) return false;
  return keys.every((key) => checklist[key]);
}

export function resolveFlowStepState(flow: FlowDefinition, job: Job, stepId: string): FlowStepState {
  const step = flow.steps.find((candidate) => candidate.id === stepId);
  if (!step) return 'todo';
  if (areChecklistKeysComplete(job.checklist, step.checklistKeys)) {
    return 'done';
  }

  const seeded = createInitialFlowStepStates(flow, job.flowStepStates);
  return seeded[stepId] ?? 'todo';
}

export function getCurrentFlowStep(flow: FlowDefinition, job: Job): FlowStep | null {
  const ordered = [...flow.steps].sort((left, right) => left.order - right.order);
  for (const step of ordered) {
    if (resolveFlowStepState(flow, job, step.id) !== 'done') {
      return step;
    }
  }
  return ordered.at(-1) ?? null;
}

export function getMissingRequiredChecklist(flow: FlowDefinition | null, checklist: Checklist): ChecklistKey[] {
  if (!flow) return [];
  return flow.requiredChecklist.filter((key) => !checklist[key]);
}
